<template>
    <div>
        <h5>
            {{ formattedDate }}
        </h5>

        <MessageBox
                v-if="errorMessage !== null"
                v-bind:message="errorMessage"
                type="error">
        </MessageBox>

        <Spinner
                v-else-if="state === 'loading'"
                v-bind:title="'Loading...' | trans">
        </Spinner>

        <template v-else-if="state === 'loaded'">
            <div
                    class="day-edit"
                    v-bind:style="{marginBottom: thereAreChanges ? '90px' : '40px'}">
                <HourEdit
                        v-for="(hourData, hour) in day.hours"
                        v-bind:key="hour"
                        v-bind:hourData="hourData"
                        v-on:hourData:updated="updateHourData(hour, $event)"
                        v-bind:date="day.date"
                        v-bind:hourNumber="hour"
                        v-bind:isBoundOfDay="isBoundOfDay(hour)"
                        v-bind:class="{'hour-hidden': hour < firstDisplayedHour || hour > lastDisplayedHour}"
                        v-bind:user="user"
                        class="hour">
                </HourEdit>
            </div>

            <div class="fixed-bottom">
                <button
                        v-on:click="$emit('day-edit:close')"
                        class="btn btn-default">
                    {{ 'Cancel' | trans }}
                </button>
                <button
                        v-if="thereAreChanges"
                        v-on:click="onOKClick"
                        class="btn validate-button pull-right">
                    {{ 'Validate' | trans }}
                </button>
                <div
                        v-if="thereAreChanges"
                        class="update-weekly">
                    <label>
                        <input
                                v-model="updateWeekly"
                                type="checkbox">
                        {{ updateWeeklyCheckboxLabel }}
                    </label>
                </div>
            </div>
        </template>

        <Spinner
                v-else-if="state === 'loadingRefundInfos'"
                v-bind:title="'Loading...' | trans">
        </Spinner>

        <div
                v-else-if="state === 'confirmationAsked'"
                class="refunds-confirmation">
            <h4>{{ 'Remboursements' }}:</h4>
            <div
                    v-for="refundInfos in listOfRefundInfos"
                    class="refund-infos">
                {{ 'Client' | trans }}: {{ refundInfos.customerName }}<br>
                {{ 'Intervention prévue le' }}: {{ $moment.unix(refundInfos.jobDateJobStartTimestamp).format('DD/MM/YYYY H:mm') }}<br>
                {{ 'Prix du job' | trans }}: {{ refundInfos.purchaseAmount.toFixed(0) }}{{ $currency.symbol(user.country)}}<br>
                {{ 'Montant du remboursement' | trans }}: {{ refundInfos.refundAmount.toFixed(0) }}{{ $currency.symbol(user.country)}} ({{ refundInfos.refundRatio*100 }}%)<br>
                <div
                        v-if="refundInfos.refundRatio === 0"
                        class="no-refund-warning">
                    {{ 'Aucun remboursement' | trans }}
                </div>
            </div>
            <nl2br tag="div" v-bind:text="$options.filters.trans('partial_refund_policy').replace(/_percents_/g, '%')"/>
            <br>
            <div class="confirmation-info">
                {{ `En cliquant sur OK, j'accepte les remboursements partiels proposés ci-dessus` | trans }}
            </div>
            <div class="row">
                <div class="col-xs-6">
                    <button
                            v-on:click="confirm"
                            class="btn btn-primary">
                        {{ 'OK' | trans }}
                    </button>
                </div>
                <div class="col-xs-6">
                    <button
                            v-on:click="$emit('day-edit:close')"
                            class="btn btn-primary">
                        {{ 'Cancel' | trans }}
                    </button>
                </div>
            </div>
        </div>

        <Spinner
                v-else-if="state === 'confirming'"
                v-bind:title="'Saving...' | trans">
        </Spinner>
    </div>
</template>

<script>
    import moment from 'moment';
    import MessageBox from './MessageBox';
    import HourEdit from './HourEdit';
    import Spinner from '../Spinner';

    export default {
        components: {MessageBox, HourEdit, Spinner},
        props: {
            initialDay: Object,
            user: Object
        },
        data() {
            return {
                errorMessage: null,
                state: 'loading',
                day: null,
                listOfRefundInfos: null,
                updateWeekly: false,
            }
        },
        async created() {
            try {
                this.day = await this.$api.holiday.day(this.initialDay.date);
            }
            catch (error) {
                if (this.$api.is401(error)) {
                    // in case of 401, just abort javascript execution
                    return;
                }
                if (this.$api.isNoConnectivity(error)) {
                    this.errorMessage = this.$options.filters.trans('No connection ?');
                } else {
                    this.$bugsnagClient.notify(error);
                    this.errorMessage = this.$options.filters.trans('Unknown error');
                }
                return;
            }
            this.state = 'loaded';
        },
        methods: {
            async onOKClick() {
                if (this.thereAreChanges) {
                    if(this.user.workerIsNovice === true){
                        this.confirm();
                        return;
                    }
                    this.state = 'loadingRefundInfos';
                    try {
                        this.listOfRefundInfos = await this.$api.holiday.getListOfRefundInfos(this.initialDay.date, this.day.hours);
                    }
                    catch (error) {
                        if (this.$api.is401(error)) {
                            // in case of 401, just abort javascript execution
                            return;
                        }
                        if (this.$api.isNoConnectivity(error)) {
                            this.errorMessage = this.$options.filters.trans('No connection ?');
                        } else {
                            this.$bugsnagClient.notify(error);
                            this.errorMessage = this.$options.filters.trans('Unknown error');
                        }
                        return;
                    }
                    if (this.listOfRefundInfos.length === 0) {
                        this.confirm();
                        return;
                    }
                    this.state = 'confirmationAsked';
                }
            },
            async confirm() {
                this.state = 'confirming';
                try {
                    if (this.updateWeekly) {
                        await this.$api.holiday.updateDayWeekly(this.initialDay.date, this.day.hours);
                    } else {
                        await this.$api.holiday.updateDayPunctual(this.initialDay.date, this.day.hours);
                    }
                }
                catch (error) {
                    if (this.$api.is401(error)) {
                        // in case of 401, just abort javascript execution
                        return;
                    }
                    if (this.$api.isNoConnectivity(error)) {
                        this.errorMessage = this.$options.filters.trans('No connection ?');
                    } else {
                        this.$bugsnagClient.notify(error);
                        this.errorMessage = this.$options.filters.trans('Unknown error');
                    }
                    return;
                }
                this.state = 'confirmed';
                this.$emit('day-edit:close');
            },
            updateHourData(hour, hourData) {
                if (hourData.available === true) {
                    hourData.zoneId = null;
                }
                this.$set(this.day.hours, hour, hourData);
            },
            isFirstHourOfARun(hour) {
                return (
                    hour === 0
                    || this.day.hours[hour].available !== this.day.hours[hour - 1].available
                );
            },
            isLastHourOfARun(hour) {
                return (
                    hour === 23
                    || this.day.hours[hour].available !== this.day.hours[hour + 1].available
                );

            },
            isInFirstUnavailableRun(hour) {
                for (let i = 0; i < hour; i++) {
                    if (this.day.hours[i].available === true) {
                        return false;
                    }
                }
                return true;
            },
            isInLastUnavailableRun(hour) {
                for (let i = hour + 1; i < this.day.hours.length; i++) {
                    if (this.day.hours[i].available === true) {
                        return false;
                    }
                }
                return true;
            },
            isBoundOfDay(hour) {
                return (
                    this.isInFirstUnavailableRun(hour) && this.isLastHourOfARun(hour)
                    || this.isInLastUnavailableRun(hour) && this.isFirstHourOfARun(hour)
                );
            },
        },
        computed: {
            thereAreChanges() {
                if (this.day === null) {
                    return false;
                }
                for (let hour = 0; hour < this.initialDay.hours.length; hour++) {
                    if (
                        this.initialDay.hours[hour].available !== this.day.hours[hour].available
                        || this.initialDay.hours[hour].zoneId !== this.day.hours[hour].zoneId
                    ) {
                        return true;
                    }
                }
                return false;
            },
            firstDisplayedHour() {
                let firstDisplayedHour = 8;
                if (this.firstAvailableHour !== null) {
                    firstDisplayedHour = Math.min(8, this.firstAvailableHour - 1);
                }
                return firstDisplayedHour;
            },
            lastDisplayedHour() {
                let lastDisplayedHour = 18;
                if (this.lastAvailableHour !== null) {
                    lastDisplayedHour = Math.max(18, this.lastAvailableHour + 1);
                }
                return lastDisplayedHour;
            },
            firstAvailableHour() {
                for (let hour = 7; hour < 24; hour++) {
                    if (this.day.hours[hour].available === true) {
                        return hour;
                    }
                }
                return null;
            },
            lastAvailableHour() {
                for (let hour = 20; hour >= 0; hour--) {
                    if (this.day.hours[hour].available === true) {
                        return hour;
                    }
                }
                return null;
            },
            formattedDate() {
                return moment(this.initialDay.date).format('dddd D MMM');
            },
            updateWeeklyCheckboxLabel() {
                return this.$options.filters.trans(
                    'availabilities_day_edit_update_weekly_checkbox_label',
                    {dayName: moment(this.day.date).format('dddd').toLowerCase()}
                );
            },
        },
    }
</script>

<style scoped lang="scss">
    .hour {
        cursor: pointer;
        padding: 6px;
        margin-top: 5px;
        font-size: 20px;
        font-weight: bold;
        transition: all 1s;
        transition-delay: .5s;
        padding-left: 15px;
        &.available {
            background: #95ffb7;
        }
        &.not-available {
            background: #ff6061;
        }
        &.hour-hidden {
            display: none;
            margin-bottom: -50px;
            pointer-events: none;
        }
    }
    .fixed-bottom {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: gainsboro;
        z-index: 10;
        button {
            margin: 10px;
            font-size: 18px;
        }
        .validate-button {
            background: #2e91e1;
            color: white;
            font-weight: bold;
        }
    }
    .refunds-confirmation {
        .refund-infos {
            padding: 10px;
            border: 1px solid lightgray;
            margin-bottom: 20px;
        }
        .no-refund-warning {
            padding: 10px;
            background: red;
            color: white;
            font-weight: bold;
            text-align: center;
            margin-top: 5px;
        }
        .confirmation-info {
            margin-bottom: 10px;
            font-weight: bold;
            text-align: center;
            color: red;
        }
        button {
            width: 100%;
        }
    }
    .update-weekly {
        margin: 0 10px 10px 10px;
        input[type=checkbox] {
            /* Double-sized Checkboxes */
            -ms-transform: scale(1.8); /* IE */
            -moz-transform: scale(1.8); /* FF */
            -webkit-transform: scale(1.8); /* Safari and Chrome */
            -o-transform: scale(1.8); /* Opera */
            padding: 10px;
            margin-left: 10px;
            margin-right: 10px;
        }
    }
</style>
